import { isAfter } from "date-fns";
import { FormikErrors, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventCostCategoryResource, EventCostsFormValues, EventFormValues } from "../../types/event";
import { EventFormCard } from "./EventFormCard";
import { EventPlannedCostStepFormFields } from "./EventPlannedCostStepFormFields";
import { getPrevLink, useSubmitEventStep } from "./eventStepperUtils";

export function getEventCostsStepValidator(t: TFunction) {
	return (values: EventFormValues): FormikErrors<EventFormValues> => {
		const errors: FormikErrors<EventFormValues> = {};
		const costsErrors: { [K in keyof EventCostCategoryResource]?: any }[] = [];
		const hasRequiredCostCategory =
			values.eventType?.costCategoryRequirements.some(({ requirement }) => requirement === "required") ?? false;

		if (hasRequiredCostCategory && !values.currency) {
			errors.currency = t("Field is required");
		}

		values.eventCostCategories.forEach((value, index) => {
			costsErrors[index] = {};
			if (
				values.eventType?.costCategoryRequirements.find((r) => value.costCategory.id === r.costCategory.id)
					?.requirement === "required"
			) {
				if (value?.plannedCost === null) {
					costsErrors[index].plannedCost = t("Field is required");
				}
				if (values.end && isAfter(new Date(), new Date(values.end)) && value?.actualCost === null) {
					costsErrors[index].actualCost = t("Field is required");
				}
			}
		});

		if (costsErrors.some((e) => Object.keys(e).length > 0)) {
			errors.eventCostCategories = costsErrors;
		}

		return errors;
	};
}

export const isEventPlannedCostStepValid = (errors: FormikErrors<EventCostsFormValues>) => {
	return !errors.eventCostCategories;
};

interface Props {
	variant?: "elevated" | "outlined";
}

export const EventPlannedCostStep: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const submitEventStep = useSubmitEventStep();
	const { values, errors, isValidating, setTouched, touched } = useFormikContext<EventCostsFormValues>();

	const handleNextClick = useCallback(async () => {
		await setTouched({
			...touched,
			currency: true,
			eventCostCategories: values.eventCostCategories.map(() => ({
				plannedCost: true,
				actualCost: true,
				commentAllowed: true,
				comment: true,
			})),
		});

		if (!isValidating && isEventPlannedCostStepValid(errors)) {
			await submitEventStep(values.eventType, "planned-cost");
		}
	}, [errors, isValidating, setTouched, submitEventStep, touched, values.eventCostCategories, values.eventType]);

	return (
		<EventFormCard
			variant={variant}
			title={t("Cost categories")}
			prevLink={getPrevLink(values.eventType, "planned-cost")}
			onNextClick={handleNextClick}
			showLanguageSwitcher={false}
		>
			<EventPlannedCostStepFormFields />
		</EventFormCard>
	);
};
